import {Palette} from 'hubbl-shared';
import moment from 'moment';
import {memo, useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {useAppDispatch, useAppSelector, useI18n} from '../../hooks';
import {GlobalSelector} from '../../store/global/GlobalSelector';
import {HubblGroupDTO} from '../../models';
import {GlobalActions} from '../../store/global/GlobalActions';
import {
  declineHubbl,
  joinHubblGroup,
  leaveHubblGroup,
  undeclineHubbl,
} from '../../store/hubbl/HubblThunks';
import {UserSelector} from '../../store/user/UserSelector';
import {isUserInvited} from '../../utils';
import {AttendeeRack} from '../attendee-rack/AttendeeRack';
import {
  AnimatedWaitingDots,
  ConfirmationModal,
  DevToolFloatingId,
  SecondaryButton,
  TextHeading7,
  TextHeading8,
  TextTicker,
  UserListModal,
} from '../index';
import {
  CardAttendeesInfo,
  CardContainer,
  CardContent,
  CardDetails,
  CardFooter,
  CardStatusIndicator,
  CardTitle,
  formatEventDate,
  formatEventTime,
  StatusBadge,
  MOBILE_BREAKPOINT,
} from './shared';

interface HubblGroupCardProps {
  hubblGroup: HubblGroupDTO;
  isPast?: boolean;
}

export const HubblGroupCard = memo(
  ({hubblGroup, isPast = false}: HubblGroupCardProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const t = useI18n();
    const userDetails = useAppSelector(UserSelector.getUserDetails);
    const currentLanguage = useAppSelector(GlobalSelector.getPreferredLanguage);
    const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
    const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
    const [isAttendeesModalOpen, setIsAttendeesModalOpen] = useState(false);

    const isUserCreator = useMemo(() => {
      if (!userDetails) return false;
      return hubblGroup.creator?.id === userDetails.id;
    }, [hubblGroup.creator, userDetails]);

    const isUserAttending = useMemo(() => {
      if (!userDetails || !hubblGroup.attendees) return false;
      return hubblGroup.attendees.some(
        attendee => attendee.id === userDetails.id,
      );
    }, [hubblGroup.attendees, userDetails]);

    const isCreatorWaitingForOthers = useMemo(() => {
      if (!isUserCreator || !hubblGroup.attendees) return false;
      // Creator is waiting if they are the only attendee
      return (
        hubblGroup.attendees.length === 1 &&
        hubblGroup.attendees[0].id === userDetails?.id
      );
    }, [isUserCreator, hubblGroup.attendees, userDetails]);

    const isUserDeclined = useMemo(() => {
      if (!userDetails) return false;
      return hubblGroup.declinedUserIds?.includes(userDetails.id) || false;
    }, [hubblGroup.declinedUserIds, userDetails]);

    const isHubblPassed = useMemo(() => {
      return moment(
        hubblGroup.endDateTime?.toDate() || hubblGroup.startDateTime.toDate(),
      ).isBefore(moment());
    }, [hubblGroup.endDateTime, hubblGroup.startDateTime]);

    const isInvitation = useMemo(() => {
      return isUserInvited(hubblGroup.invitedUsers);
    }, [hubblGroup.invitedUsers]);

    const onJoinHubbl = useCallback(async () => {
      if (!userDetails) return;

      dispatch(GlobalActions.setIsBusy(true));

      try {
        await dispatch(
          joinHubblGroup({
            hubblId: hubblGroup.id,
            user: userDetails,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Error joining hubbl group:', error);
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    }, [dispatch, hubblGroup.id, userDetails]);

    const onDeclineHubbl = useCallback(async () => {
      if (!userDetails) return;

      dispatch(GlobalActions.setIsBusy(true));

      try {
        await dispatch(
          declineHubbl({
            hubblId: hubblGroup.id,
            user: userDetails,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Error declining hubbl group:', error);
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    }, [dispatch, hubblGroup.id, userDetails]);

    const onUndeclineHubbl = useCallback(async () => {
      if (!userDetails) return;

      dispatch(GlobalActions.setIsBusy(true));

      try {
        await dispatch(
          undeclineHubbl({
            hubblId: hubblGroup.id,
            user: userDetails,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Error undoing decline for hubbl group:', error);
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    }, [dispatch, hubblGroup.id, userDetails]);

    const onLeaveHubbl = useCallback(async () => {
      if (!userDetails) return;

      dispatch(GlobalActions.setIsBusy(true));

      try {
        await dispatch(
          leaveHubblGroup({
            hubblId: hubblGroup.id,
            user: userDetails,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Error leaving hubbl group:', error);
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    }, [dispatch, hubblGroup.id, userDetails]);

    const handleLeaveClick = useCallback(() => {
      setIsLeaveModalOpen(true);
    }, []);

    const handleLeaveConfirm = useCallback(async () => {
      setIsLeaveModalOpen(false);
      await onLeaveHubbl();
    }, [onLeaveHubbl]);

    const handleLeaveCancel = useCallback(() => {
      setIsLeaveModalOpen(false);
    }, []);

    const handleDeclineClick = useCallback(() => {
      setIsDeclineModalOpen(true);
    }, []);

    const handleDeclineConfirm = useCallback(async () => {
      setIsDeclineModalOpen(false);
      await onDeclineHubbl();
    }, [onDeclineHubbl]);

    const handleDeclineCancel = useCallback(() => {
      setIsDeclineModalOpen(false);
    }, []);

    const handleAttendeesClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setIsAttendeesModalOpen(true);
    }, []);

    const handleCardClick = useCallback(() => {
      navigate(`/employee/group/${hubblGroup.id}`);
    }, [navigate, hubblGroup.id]);

    return (
      <>
        <CardContainer
          onClick={handleCardClick}
          isPast={isPast || isUserDeclined}
          isDeclined={isUserDeclined}
          variant="group"
          compressed={true}>
          <HubblIcon
            color={hubblGroup.color}
            compressed={true}
            emoji={hubblGroup.emoji}>
            <div style={{position: 'relative', zIndex: 199}}>
              {hubblGroup.emoji}
            </div>
            {isInvitation && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 200,
                }}>
                <TextTicker />
              </div>
            )}
          </HubblIcon>

          <CardContent>
            <CardTitle
              compressed={true}>{`${hubblGroup.description?.en}`}</CardTitle>

            <CardDetails>
              {hubblGroup.location ? (
                <>
                  <TextHeading8
                    color={Palette.almostBlack750}
                    text={`📅 ${formatEventDate(hubblGroup.startDateTime, currentLanguage)} • 🕐 ${formatEventTime(hubblGroup.startDateTime)} ${hubblGroup.endDateTime ? `- ${formatEventTime(hubblGroup.endDateTime)}` : ''}`}
                  />
                  <TextHeading8
                    text={`📍 ${hubblGroup.location}`}
                    color={Palette.almostBlack750}
                  />
                </>
              ) : (
                <>
                  <TextHeading8
                    color={Palette.almostBlack750}
                    text={`📅 ${formatEventDate(hubblGroup.startDateTime, currentLanguage)}`}
                  />
                  <TextHeading8
                    color={Palette.almostBlack750}
                    text={`🕐 ${formatEventTime(hubblGroup.startDateTime)} ${hubblGroup.endDateTime ? `- ${formatEventTime(hubblGroup.endDateTime)}` : ''}`}
                  />
                </>
              )}
            </CardDetails>

            <CardFooter>
              <CardAttendeesInfo onClick={handleAttendeesClick}>
                <AttendeeRack
                  size={24}
                  disableEmptyState
                  truncateAt={5}
                  users={hubblGroup.attendees || []}
                />
                <TextHeading7
                  text={
                    isPast
                      ? `${hubblGroup.attendees?.length || 0} ${t.groups.colleaguesAttended}`
                      : hubblGroup.maxAttendees
                        ? `${hubblGroup.attendees?.length || 0}/${hubblGroup.maxAttendees} ${t.groups.colleagues}`
                        : `${hubblGroup.attendees?.length || 0} ${t.groups.colleagues}`
                  }
                />
              </CardAttendeesInfo>

              <CardStatusIndicator>
                {/* Show decline option for initial state - only if not creator waiting */}
                {!isCreatorWaitingForOthers &&
                  !isUserAttending &&
                  !isUserDeclined &&
                  !isHubblPassed && (
                    <SecondaryButton
                      isCompact
                      customWidth={80}
                      label={t.events.declineActivity}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleDeclineClick();
                      }}
                    />
                  )}

                <StatusBadge
                  status={
                    isCreatorWaitingForOthers
                      ? 'attending'
                      : isUserAttending
                        ? 'attending'
                        : isUserDeclined
                          ? 'declined'
                          : 'none'
                  }
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (isUserDeclined) {
                      onUndeclineHubbl();
                    } else if (isCreatorWaitingForOthers) {
                      // Creator managing group - navigate to details scene
                      handleCardClick();
                    } else if (isUserAttending) {
                      // User is attending - navigate to details scene
                      handleCardClick();
                    } else if (isHubblPassed) {
                      // Group has passed - navigate to details scene
                      handleCardClick();
                    } else {
                      onJoinHubbl();
                    }
                  }}
                  isClickable={true}>
                  {isCreatorWaitingForOthers ? (
                    <AnimatedWaitingDots text={t.groups.waitingForOthers} />
                  ) : isUserAttending ? (
                    t.groups.joinedViewDetails
                  ) : isUserDeclined ? (
                    t.groups.declinedClickToChange
                  ) : isHubblPassed ? (
                    t.groups.completedViewDetails
                  ) : (
                    t.groups.join
                  )}
                </StatusBadge>
              </CardStatusIndicator>
            </CardFooter>
          </CardContent>
          <DevToolFloatingId id={hubblGroup.id} />
        </CardContainer>

        <ConfirmationModal
          isOpen={isLeaveModalOpen}
          onClose={handleLeaveCancel}
          onConfirm={handleLeaveConfirm}
          title={t.events.leaveActivity}
          message={t.events.leaveActivityMessage}
          confirmLabel={t.events.leaveActivity}
          cancelLabel={t.events.stayInActivity}
        />

        <ConfirmationModal
          isOpen={isDeclineModalOpen}
          onClose={handleDeclineCancel}
          onConfirm={handleDeclineConfirm}
          title={t.events.declineActivity}
          message={t.events.declineActivityMessage}
          confirmLabel={t.events.declineActivity}
          cancelLabel={t.events.keepInvitation}
        />

        <UserListModal
          isOpen={isAttendeesModalOpen}
          onClose={() => setIsAttendeesModalOpen(false)}
          title={`${t.events.attendees} (${hubblGroup.attendees?.length || 0})`}
          users={hubblGroup.attendees || []}
        />
      </>
    );
  },
);

const HubblIcon = styled.div<{
  color?: string;
  compressed?: boolean;
  emoji?: string;
}>`
  height: ${props => (props.compressed ? '130px' : '200px')};
  width: 200px;
  object-fit: cover;
  flex-shrink: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: ${props =>
    props.color
      ? `linear-gradient(135deg, ${props.color} 0%, ${props.color}CC 100%)`
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: ${props => (props.compressed ? '2.5rem' : '4rem')};
  position: relative;
  overflow: hidden;

  /* Emoji pattern overlay */
  &::after {
    content: '';
    position: absolute;
    top: -12px;
    left: -20px;
    right: 0;
    bottom: 0;
    background-image: ${props =>
      props.emoji
        ? `url("data:image/svg+xml,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 40 40">
              <text x="20" y="25" font-size="20" text-anchor="middle" fill="rgba(255,255,255,0.3)">${props.emoji}</text>
            </svg>
          `)}")`
        : 'none'};
    background-size: 40px 40px;
    background-repeat: repeat;
    pointer-events: none;
    z-index: 1;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 120px;
    width: 100%;
    aspect-ratio: 16/9;
    font-size: 3rem;

    &::after {
      background-size: 30px 30px;
    }
  }
`;
